import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { LOGO_URL } from "@/lib/constants";
import { toast } from "sonner";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Welcome back.");
            navigate("/admin");
        } else {
            toast.error(res.error || "Login failed");
        }
    };

    return (
        <div
            data-testid="login-page"
            className="min-h-screen flex bg-brand-paper"
        >
            <div className="hidden lg:flex lg:w-1/2 bg-brand-black relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08]">
                    <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-brand-gold blur-3xl" />
                </div>
                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <Link to="/" className="inline-flex items-center gap-3 group" data-testid="login-back-home">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        <span className="text-xs tracking-[0.25em] uppercase">Back to site</span>
                    </Link>
                    <div>
                        <div className="bg-white p-3 inline-block mb-8">
                            <img src={LOGO_URL} alt="SheFleet" className="h-16 w-16 object-contain" />
                        </div>
                        <h1 className="font-serif text-5xl xl:text-6xl leading-tight">
                            Dispatch
                            <br />
                            <span className="italic text-brand-gold">command center.</span>
                        </h1>
                        <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                            Manage incoming bookings, review pickup requests, and stay in
                            control of every route — all from one secure dashboard.
                        </p>
                    </div>
                    <div className="text-xs tracking-[0.3em] uppercase text-brand-gold/70">
                        SheFleet Transportation LLC
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8 text-brand-black/70 text-xs tracking-[0.25em] uppercase" data-testid="login-mobile-back">
                        <ArrowLeft size={14} /> Back
                    </Link>

                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-10 h-px bg-brand-gold" />
                            <span className="text-[10px] tracking-[0.4em] uppercase text-brand-gold-deep">
                                Admin Access
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl text-brand-black">
                            Welcome back.
                        </h2>
                        <p className="mt-3 text-brand-black/65">
                            Sign in to manage bookings and inquiries.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-brand-black/70 mb-2 block">
                                Email
                            </label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-black/40" />
                                <Input
                                    type="email"
                                    required
                                    data-testid="login-email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-11 h-12 rounded-none border-brand-black/20 focus-visible:ring-brand-gold"
                                    placeholder="you@shefleettransportation.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-brand-black/70 mb-2 block">
                                Password
                            </label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-black/40" />
                                <Input
                                    type="password"
                                    required
                                    data-testid="login-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-11 h-12 rounded-none border-brand-black/20 focus-visible:ring-brand-gold"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="w-full py-4 bg-brand-black text-white text-xs tracking-[0.3em] uppercase hover:bg-brand-gold-deep transition-colors disabled:opacity-60"
                        >
                            {submitting ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
